import { useEffect, useState } from "react";
import { Gift, Users, TrendingUp, Award, Loader2 } from "lucide-react";
import { api } from "../lib/api";

// Admin-only referral analytics section.
// Left column: 3 stat cards (total referrals, most-used channel, discount
//   earners). Right side: a table of every user who has made at least one
//   referral, with per-channel breakdown chips.
//
// We deliberately don't paginate here — even at 10 k users we expect
// maybe a few hundred unique referrers, which fits comfortably in one
// scrolled table. If that ever grows too large we can add a limit param
// server-side and a "load more" here.
const CHANNEL_LABELS = {
  whatsapp: { name: "WhatsApp", cls: "bg-[#25D366]/15 text-[#25D366] border-[#25D366]/40" },
  twitter: { name: "Twitter", cls: "bg-white/5 text-zinc-200 border-zinc-600" },
  copy: { name: "Copy", cls: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
  link: { name: "Link", cls: "bg-purple-500/15 text-purple-300 border-purple-500/40" },
  direct: { name: "Direct", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" },
  other: { name: "Other", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" },
};

function ChannelBadge({ channel, count }) {
  const meta = CHANNEL_LABELS[channel] || CHANNEL_LABELS.other;
  return (
    <span
      data-testid={`ref-ch-${channel}`}
      className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${meta.cls}`}
    >
      {meta.name} <span className="text-white/80">{count}</span>
    </span>
  );
}

function StatBox({ label, value, hint, icon: Icon, testId }) {
  return (
    <div
      className="border border-zinc-800 bg-zinc-900/40 p-5"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </div>
      <div className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-white break-words">
        {value}
      </div>
      {hint && <div className="text-[11px] font-mono text-zinc-500 mt-2">{hint}</div>}
    </div>
  );
}

export default function ReferralAnalytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/referrals");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load referral analytics");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <section data-testid="admin-referrals-loading" className="border border-zinc-800 bg-zinc-900/40 p-6 flex items-center gap-3 text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-xs font-mono">Loading referral analytics…</span>
      </section>
    );
  }
  if (err) {
    return (
      <section className="border border-red-500/30 bg-red-500/5 p-6 text-red-300 text-xs font-mono">
        {err}
      </section>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  const rows = data.rows || [];
  const mostUsed = t.most_used_channel
    ? (CHANNEL_LABELS[t.most_used_channel]?.name || t.most_used_channel)
    : "—";

  return (
    <section data-testid="admin-referrals-section" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2 flex items-center gap-1.5">
            <Gift className="w-3 h-3" /> Referrals
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Program analytics
          </h2>
        </div>
        <div className="text-[11px] font-mono text-zinc-500">
          {t.unique_referrers ?? 0} unique referrers
        </div>
      </div>

      {/* Overall stats — 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatBox
          label="Total referrals made"
          value={(t.total_referrals ?? 0).toLocaleString("en-IN")}
          hint="Sign-ups attributed to a referral link"
          icon={Users}
          testId="admin-ref-total"
        />
        <StatBox
          label="Most-used channel"
          value={mostUsed}
          hint={
            t.channel_totals
              ? Object.entries(t.channel_totals)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3)
                  .map(([k, v]) => `${CHANNEL_LABELS[k]?.name || k}: ${v}`)
                  .join(" · ")
              : "No channel data yet"
          }
          icon={TrendingUp}
          testId="admin-ref-channel"
        />
        <StatBox
          label={`${data.discount_percent}% discount earners`}
          value={(t.discount_earners ?? 0).toLocaleString("en-IN")}
          hint="Users who've unlocked at least one reward"
          icon={Award}
          testId="admin-ref-earners"
        />
      </div>

      {/* Per-referrer table */}
      <div className="border border-zinc-800 bg-zinc-900/40">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Top referrers · sorted by total signups
          </div>
          <div className="text-[10px] font-mono text-zinc-600">{rows.length} row{rows.length !== 1 ? "s" : ""}</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm font-mono">
            No referrals yet — as users share their links, this table fills in.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              data-testid="admin-referrals-table"
              className="w-full text-sm"
            >
              <thead className="border-b border-zinc-800 bg-zinc-950/40">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-4 py-3 font-normal">User</th>
                  <th className="text-right px-4 py-3 font-normal w-24">Total</th>
                  <th className="text-left px-4 py-3 font-normal">Channels</th>
                  <th className="text-right px-4 py-3 font-normal w-32">Rewards</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.referral_code}
                    data-testid={`ref-row-${r.referral_code}`}
                    className="border-b border-zinc-800/60 hover:bg-zinc-900/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="text-white text-sm truncate max-w-[240px]">{r.name}</div>
                      <div className="text-[11px] font-mono text-zinc-500 truncate max-w-[240px]">
                        {r.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        data-testid={`ref-row-total-${r.referral_code}`}
                        className="font-heading text-xl font-bold text-white"
                      >
                        {r.total_referrals}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(r.channels).sort((a, b) => b[1] - a[1]).map(([ch, count]) => (
                          <ChannelBadge key={ch} channel={ch} count={count} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-[11px] font-mono text-zinc-400">
                        <span className="text-emerald-400">{r.discounts_earned}</span> earned
                        {r.discounts_used > 0 && (
                          <span className="text-zinc-500"> · {r.discounts_used} used</span>
                        )}
                        {r.discounts_available > 0 && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[9px] uppercase tracking-widest">
                            {r.discounts_available} ready
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
